'use client';

import Image from 'next/image';

import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { NrfMaterialIcon } from '~/components/nrf-material-icon';

export function MainHowItWorks() {
  const howToStepsData = [
    {
      icon: (
        <NrfMaterialIcon
          materialName="cloud_upload"
          className="text-2xl md:text-4xl lg:text-6xl"
        />
      ),
      headline: (
        <>
          <span className="text-primary">
            <Trans i18nKey={'common:step'} /> 1:
          </span>{' '}
          <Trans i18nKey={'marketing:sectionHow:step1Head'}></Trans>
        </>
      ),
      subtext: <Trans i18nKey={'marketing:sectionHow:step1Text'} />,
    },
    {
      icon: (
        <NrfMaterialIcon
          materialName="settings"
          className="text-2xl md:text-4xl lg:text-6xl"
        />
      ),
      headline: (
        <>
          <span className="text-primary">
            <Trans i18nKey={'common:step'} /> 2:
          </span>{' '}
          <Trans i18nKey={'marketing:sectionHow:step2Head'}></Trans>
        </>
      ),
      subtext: <Trans i18nKey={'marketing:sectionHow:step2Text'} />,
    },
    {
      icon: (
        <NrfMaterialIcon
          materialName="troubleshoot"
          className="text-2xl md:text-4xl lg:text-6xl"
        />
      ),
      headline: (
        <>
          <span className="text-primary">
            <Trans i18nKey={'common:step'} /> 3:
          </span>{' '}
          <Trans i18nKey={'marketing:sectionHow:step3Head'}></Trans>
        </>
      ),
      subtext: <Trans i18nKey={'marketing:sectionHow:step3Text'} />,
    },
    {
      icon: (
        <NrfMaterialIcon
          materialName="check_circle"
          className="text-2xl md:text-4xl lg:text-6xl"
        />
      ),
      headline: (
        <>
          <span className="text-primary">
            <Trans i18nKey={'common:step'} /> 4:
          </span>{' '}
          <Trans i18nKey={'marketing:sectionHow:step4Head'}></Trans>
        </>
      ),
      subtext: <Trans i18nKey={'marketing:sectionHow:step4Text'} />,
    },
  ];

  return (
    <div
      className="bg-nrf-radial-gradient-right scroll-mt-16 bg-white bg-nrf-radial-gradient-light py-16 lg:py-20 xl:py-24"
      id="how-it-works"
    >
      <div className={'container lg:pl-20 xl:pl-44'}>
        <h2 className="mb-10 text-center font-heading text-3xl text-secondary md:mb-14 md:text-4xl lg:mb-20 lg:text-5xl">
          {/* One sentence per language, so word order and spacing around the
              logo ("work?" vs. "?") live in the translation. */}
          <Trans
            i18nKey={'marketing:sectionHow:headline'}
            components={{ logo: <NarrafixWordmark /> }}
          />
        </h2>

        <HowToSteps steps={howToStepsData} />
      </div>
    </div>
  );
}

// The logo on wider screens, plain text on narrow ones. Sized in em so it
// scales with the heading. The lettering ends at ~88 % of the SVG height, so
// the image sits 12 % of its height (0.1em) below the text baseline.
function NarrafixWordmark() {
  return (
    <>
      <Image
        src="/images/logo-narrafix.svg"
        alt="Narrafix"
        width={300}
        height={63}
        className="hidden h-[0.8em] w-auto align-[-0.1em] md:inline-block"
      />
      <span className="md:hidden">Narrafix</span>
    </>
  );
}

interface HowToStepProps {
  icon: React.ReactNode;
  iconWrapperClassName?: string;
  headline: React.ReactNode;
  subtext: React.ReactNode;
}

interface HowToStepsProps {
  steps: HowToStepProps[];
  className?: string;
}

function HowToSteps({ steps, className }: HowToStepsProps) {
  return (
    <div className={cn(`grid grid-cols-1 gap-8 md:gap-0`, className)}>
      {steps.map((step, index) => (
        <div key={index}>
          {/* Icon and text */}
          <div className={`flex items-center`}>
            {/* Icon */}
            <div className="mr-3 flex flex-col items-center overflow-visible sm:mr-6">
              <div
                className={`flex h-12 w-12 items-center justify-center rounded-full bg-white p-3 shadow-glow shadow-primary/25 sm:h-14 sm:w-14 md:h-20 md:w-20 md:p-4 lg:h-28 lg:w-28 lg:p-6`}
              >
                {step.icon}
              </div>
            </div>

            {/* Text */}
            <div className={'flex flex-col space-y-1'}>
              {step.headline && (
                <h3 className="font-heading text-xl font-semibold md:text-2.5xl lg:text-3xl">
                  {step.headline}
                </h3>
              )}
              <p className="md:text-xl">{step.subtext}</p>
            </div>
          </div>

          {/* Dotted line */}
          <div className={'hidden md:block'}>
            {index < steps.length - 1 && (
              <div className="sm: flex w-12 flex-col items-center pt-2 sm:w-14 md:w-20 lg:w-28">
                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="mb-2 h-1 w-1 rounded-full bg-primary lg:h-2 lg:w-2"
                  ></div>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
